import React from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  IconButton,
  Button,
  InputLabel,
  FormControl,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import trash from '../../Assets/AlertIcon/trash2.svg';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  openDeleteSiteModal,
  selectedSiteForDeletion,
  allSitesRanDb,
  ranDbViewMode,
  ranDbArchivedType,
  ranDbTotalCount,
  ranDbTotalPages,
  ranDbCurrentPage,
  ranDbPageSize,
} from '../../common/atom/atoms';
import { deleteSiteRanDb } from './functions/delete_site_ran_db.function';
import { getAllRanSites } from './functions/get_all_ran_sites.function';
import { notify } from '../../common/functions/react-toastify.function';
import { useBackdrop } from '../../common/components/backDrop_context.component';

export default function DeleteSiteModal() {
  const open = useAtomValue(openDeleteSiteModal);
  const selectedSite = useAtomValue(selectedSiteForDeletion);
  const viewMode = useAtomValue(ranDbViewMode);
  const archivedType = useAtomValue(ranDbArchivedType);
  const setOpenDeleteModal = useSetAtom(openDeleteSiteModal);
  const setSelectedSiteForDeletion = useSetAtom(selectedSiteForDeletion);
  const setAllRanSite = useSetAtom(allSitesRanDb);
  const setTotalCount = useSetAtom(ranDbTotalCount);
  const setTotalPages = useSetAtom(ranDbTotalPages);
  const setCurrentPage = useSetAtom(ranDbCurrentPage);
  const setPageSize = useSetAtom(ranDbPageSize);
  const { showBackdrop, hideBackdrop } = useBackdrop();

  const [type, setType] = React.useState('Dismantled');
  const [siteFrom, setSiteFrom] = React.useState('');
  const [siteTo, setSiteTo] = React.useState('');
  const [reason, setReason] = React.useState('');

  const handleTypeChange = (event) => {
    const newType = event.target.value;
    setType(newType);

    // Clear site fields when switching away from Relocation
    if (newType !== 'Relocate') {
      setSiteFrom('');
      setSiteTo('');
    }
  };

  const handleClose = () => {
    setOpenDeleteModal(false);
    setSelectedSiteForDeletion(null);
    // Reset form
    setType('Dismantled');
    setSiteFrom('');
    setSiteTo('');
    setReason('');
  };

  const fetchAllRanSites = async () => {
    try {
      let filters = {};
      if (viewMode === 'archived') {
        if (archivedType === 'all') {
          filters = {
            archived: true,
          };
        } else if (archivedType === 'Dismantle') {
          filters = {
            archived_type: 'Dismantled',
          };
        } else if (archivedType === 'Relocation') {
          filters = {
            archived_type: 'Relocation',
          };
        } else if (archivedType === 'Other') {
          filters = {
            archived_type: 'Other',
          };
        }
      } else {
        filters = {
          archived: false,
        };
      }
      const resp = await getAllRanSites(filters);
      setAllRanSite(resp?.data || []);

      // Update pagination state with response data
      if (resp?.pagination) {
        setTotalCount(resp.pagination.totalCount);
        setTotalPages(resp.pagination.totalPages);
        setCurrentPage(resp.pagination.currentPage);
        setPageSize(resp.pagination.limit);
      }
    } catch (error) {
      console.error('Error fetching RanDB sites:', error);
    }
  };

  const handleConfirm = async () => {
    if (!reason.trim()) {
      notify('error', 'Please enter a reason for deletion');
      return;
    }

    if (type === 'Relocate' && (!siteFrom.trim() || !siteTo.trim())) {
      notify('error', 'Please enter both from and to site codes for relocation');
      return;
    }

    if (type === 'Relocate' && siteFrom.trim() === siteTo.trim()) {
      notify('error', 'From site and To site cannot be the same');
      return;
    }

    showBackdrop();
    try {
      let payload = {
        archived_type: type === 'Relocate' ? 'Relocation' : 'Dismantled',
        reason: reason.trim(),
      };

      if (type === 'Relocate') {
        payload.site_from = siteFrom.trim();
        payload.site_to = siteTo.trim();
      }

      console.log('Delete payload:', payload);
      await deleteSiteRanDb(selectedSite.id, payload);

      // Fetch updated data from API instead of just updating frontend state
      await fetchAllRanSites();

      notify('success', 'Site deleted successfully');
      handleClose();
    } catch (error) {
      console.error('Error deleting site:', error);
      notify('error', 'Failed to delete site. Please try again.');
    } finally {
      hideBackdrop();
    }
  };

  if (!open || !selectedSite) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{ outline: 'none' }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: '#fff',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          backgroundColor: '#fff',
        }}
      >
        {/* Header with Icon and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <div style={{ height: '100%', overflow: 'hidden', marginRight: '5%' }}>
            <div
              style={{
                width: 36,
                height: 36,
                background: '#FDC4BF',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img alt="" src={trash} />
            </div>
          </div>

          <Typography id="modal-modal-title" variant="h6" component="h2">
            Delete Site: {selectedSite.site_code}
          </Typography>
          <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Form Content */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Type Dropdown */}
          <FormControl fullWidth>
            <InputLabel id="type-label">Type</InputLabel>
            <Select
              labelId="type-label"
              value={type}
              label="Type"
              onChange={handleTypeChange}
              sx={{ backgroundColor: '#fff' }}
            >
              <MenuItem value="Dismantled">Dismantled</MenuItem>
              <MenuItem value="Relocate">Relocation</MenuItem>
            </Select>
          </FormControl>

          {/* From Site and To Site Fields - only show for Relocation */}
          {type === 'Relocate' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Please provide the site codes for relocation:
              </Typography>
              <Stack direction="row" spacing={2} mb={2}>
                <TextField
                  label="From site *"
                  placeholder="site code"
                  variant="outlined"
                  fullWidth
                  required
                  value={siteFrom}
                  onChange={(e) => setSiteFrom(e.target.value)}
                  sx={{ backgroundColor: '#fff' }}
                />

                <TextField
                  label="To site *"
                  placeholder="site code"
                  variant="outlined"
                  fullWidth
                  required
                  value={siteTo}
                  onChange={(e) => setSiteTo(e.target.value)}
                  sx={{ backgroundColor: '#fff' }}
                />
              </Stack>
            </>
          )}

          {/* Reason Text Area */}
          <TextField
            label="Reason *"
            placeholder="Enter a description..."
            multiline
            rows={3}
            variant="outlined"
            fullWidth
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ backgroundColor: '#fff' }}
          />

          {/* Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                width: '48%',
                borderColor: '#ccc',
                color: '#000',
                backgroundColor: '#fff',
                textTransform: 'none',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              sx={{
                width: '48%',
                backgroundColor: '#d32f2f',
                color: '#fff',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#b71c1c',
                },
              }}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
